"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/OnboardingWizard/steps/GoalSelectionStep";

import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import type { ScoredGoalHook } from "@/src/hooks/onboarding/scoreOnboardingGoals";
import { EdHeroBubble } from "../quiz/EdHeroBubble";
import { QuizProgressHeader, QuizStepLayout } from "../quiz/QuizStepLayout";

const I18n = I18nUtil.createScoped(Translations);

/** Hold selected surface briefly so users can see overlay_20% before advance. */
export const GOAL_SELECT_ADVANCE_DELAY_MS = 1000;

interface GoalSelectionStepProps {
  /** PRD §4.1 — engine top-4 goals in display order */
  scoredGoals: ScoredGoalHook[];
  /** Scored lead goal id — marks recommended row (data-goal-lead); surface follows selected only */
  leadGoalId: string;
  selectedId: string;
  progressIndex: number;
  progressTotal: number;
  onBack: () => void;
  onSelect: (goalId: string, rank: number) => void;
}

export const GoalSelectionStep: FC<GoalSelectionStepProps> = ({
  scoredGoals,
  leadGoalId,
  selectedId,
  progressIndex,
  progressTotal,
  onBack,
  onSelect,
}) => {
  const [pendingSelectedId, setPendingSelectedId] = useState("");
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (advanceTimerRef.current !== null) {
        clearTimeout(advanceTimerRef.current);
      }
    };
  }, []);

  const displaySelectedId = pendingSelectedId || selectedId;

  const handleSelect = (goalId: string, rank: number) => {
    if (advancingRef.current) {
      return;
    }
    advancingRef.current = true;
    setPendingSelectedId(goalId);
    advanceTimerRef.current = setTimeout(() => {
      advanceTimerRef.current = null;
      onSelect(goalId, rank);
    }, GOAL_SELECT_ADVANCE_DELAY_MS);
  };

  return (
    <QuizStepLayout
      testId="onboarding-goal-selection-step"
      reserveFloatingCtaSpace={false}
      header={
        <QuizProgressHeader
          progressIndex={progressIndex}
          progressTotal={progressTotal}
          onBack={onBack}
        />
      }
    >
      <div
        className="flex flex-col gap-8 px-2 pb-8 pt-6 md:gap-[60px] md:px-0 md:pb-8 md:pt-0 overflow-y-auto no-scrollbar"
        data-testid="goal-selection-body"
        data-figma-node="96121:8443"
        data-figma-node-pc="96344:110507"
      >
        <EdHeroBubble
          message={I18n.t(
            "This is just my read from our first meeting — the more time we spend, the sharper it gets. So, where do we start?",
          )}
        />

        <div
          className="flex flex-col gap-[22px] md:gap-8"
          data-testid="goal-selection-question-block"
        >
          <h2
            className="headline_regular_m text-content_inverse_primary"
            data-testid="goal-selection-question"
          >
            {I18n.t("What do you want to crack first?")}
          </h2>

          <div
            className="flex flex-col gap-3"
            data-testid="goal-selection-options"
          >
            {scoredGoals.map(({ option: goal }, index) => {
              const selected = displaySelectedId === goal.id;
              const isLead = goal.id === leadGoalId;
              return (
                <button
                  key={goal.id}
                  type="button"
                  data-testid="goal-option"
                  data-goal-id={goal.id}
                  data-goal-rank={index + 1}
                  data-goal-lead={isLead ? "true" : "false"}
                  className={cn(
                    "flex w-full items-center gap-6 rounded-xl p-3 text-left transition-colors",
                    // Figma: inverse overlay 10% default / 20% selected (no overlay10 token → bg-white/10)
                    selected ? "bg-white/20" : "bg-white/10",
                  )}
                  onClick={() => handleSelect(goal.id, index + 1)}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-3.5">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-content_aura_lv3_primary">
                      <Icon
                        name={goal.icon}
                        size={24}
                        className="text-content_brand_primary"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="title_l text-content_inverse_primary">
                        {goal.title}
                      </p>
                      <p className="body_m text-content_inverse_secondary">
                        {goal.subtitle}
                      </p>
                    </div>
                  </div>
                  <Icon
                    name="right-arrow-alt"
                    size={20}
                    className="shrink-0 text-content_inverse_primary"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </QuizStepLayout>
  );
};
